import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { getAllCountries } from "../../redux/actions/CountryActions";
import { getAllStates } from "../../redux/actions/StateActions";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import StateForm from "./StateForm";
import Loader from "../../components/loader/Loader";
import { StateModel } from "../../models/StateModel";
import { isValidArr } from "../../utility/utils";

export type Mode = "add" | "delete";

type iData = {
  id: string;
  name: string;
  code: string;
  country: string;
};

const StatePage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<StateModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allCountries = useAppSelector((state) => state.country.all_Countries);
  const allStates = useAppSelector((state) => state.state.all_States);
  const isStatesFetching = useAppSelector(
    (state) => state.state.isStatesFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "State Name",
      },
      {
        id: "code",
        numeric: false,
        disablePadding: false,
        label: "State Code",
      },
      {
        id: "country",
        numeric: false,
        disablePadding: false,
        label: "Country",
      },
    ];
  }, []);

  //To fetch all states and countries
  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCountries());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    setRootData(() =>
      allStates.map((rec) => {
        const country = allCountries.find(
          (countryRec) => countryRec.countryCode === rec.countryCode
        );
        return {
          id: rec.stateCode!,
          name: rec.stateName!,
          code: rec.stateCode!,
          country: country?.countryName!,
        };
      })
    );
  }, [allStates, allCountries]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allStates.find((rec) => rec.stateCode === id);
    setMode("delete");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allStates) && isStatesFetching ? <Loader /> : null}
      <PageTitle title="States" btnText="Add State" onClick={createHandler} />
      <PageSearch placeholder="Search States" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
        isEditable={false}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create State" : "Delete State"}
          handleModalClose={handleModalClose}
        >
          <StateForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default StatePage;
